import React, { useCallback, useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime, useIntl } from 'react-intl';
import { useMessage } from '@tmapy/intl';

import './HistoryGraph.scss';

import {
  DangerAlert,
  Icon,
  InfoAlert,
  Link,
  SecondaryBtn,
  SvgCaretDown,
  WaitingAlert,
} from '@tmapy/style-guide';

import { QUERY_STATE } from '../../constants';
import type { MonitoringTimeline } from '../../monitoring/hooks/useMonitoringTimeline';
import {
  monitoringDirectionMessages,
  monitoringEventTypeMessages,
  monitoringMessages,
  monitoringOutcomeMessages,
  monitoringSourceMessages,
} from '../../monitoring/messages';
import type { StateChange, TimelineDay, TimelineEvent } from '../../monitoring/utils/timeline';
import { buildTimelineDays } from '../../monitoring/utils/timeline';
import { DocHistoryResponse, MonitoringLogRowResponse } from '../../openapi/types';
import { DirectionEnum } from '../../openapi/types/DirectionEnum';
import { EventTypeEnum } from '../../openapi/types/EventTypeEnum';
import { OutcomeEnum } from '../../openapi/types/OutcomeEnum';
import { DOC_STATE } from '../constants';
import { DocBadge } from './DocBadge';
import { docStateMessages } from './docsMessages';

type HistoryGraphProps = {
  timeline: MonitoringTimeline;
  history?: DocHistoryResponse[];
  /** Rozhoduje o formulaci názvu stavu, viz `docStateMessages`. */
  flow?: DocHistoryResponse['flow'];
};

const loadMoreIcon = { element: <SvgCaretDown />, position: 'right' as const };

/** Do `title` patří celý údaj; na řádku se čas krátí na minuty, u událostí na sekundy. */
const FULL_TIME_FORMAT: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'numeric',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
};

const MESSAGE_PREVIEW_LENGTH = 160;
const VISIBLE_STATES = 3;

const NOTABLE_OUTCOMES: OutcomeEnum[] = [
  OutcomeEnum.FAILED,
  OutcomeEnum.REJECTED,
  OutcomeEnum.CANCELLED,
  OutcomeEnum.ACCEPTED,
];

const DIRECTION_ICON: Record<DirectionEnum, string> = {
  OUT: '↗',
  IN: '↘',
  INTERNAL: '●',
};

const getEventIcon = (event: MonitoringLogRowResponse) =>
  event.event_type === EventTypeEnum.JOB || event.event_type === EventTypeEnum.FLOW
    ? '◆'
    : DIRECTION_ICON[event.direction];

const HistoryMessage: React.FC<{ message: string }> = ({ message }) => {
  const formatMessage = useMessage();
  const [isExpanded, setIsExpanded] = useState(false);

  const isExpandable = message.length > MESSAGE_PREVIEW_LENGTH || message.includes('\n');
  if (!isExpandable) return <p className='history-message'>{message}</p>;

  return (
    <div className='history-message-block'>
      <p className={`history-message ${isExpanded ? '' : 'is-clamped'}`}>{message}</p>
      <button
        type='button'
        className='history-inline-btn'
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {formatMessage(
          isExpanded ? monitoringMessages.hideMessage : monitoringMessages.showMessage,
        )}
      </button>
    </div>
  );
};

const HistoryUpdater: React.FC<{ updater: DocHistoryResponse['updater'] }> = ({ updater }) => {
  const formatMessage = useMessage();

  if (!updater) return <>{formatMessage(monitoringMessages.updaterNull)}</>;

  const name = `${updater.name ?? ''} ${updater.surname ?? ''}`.trim();
  if (!updater.email) return <>{name}</>;

  // Vidět je jméno, adresa zůstává v `title` - v řádku stavu na ni není místo.
  return (
    <Link href={`mailto:${updater.email}`} severity='colorful' tooltip={updater.email}>
      {name || updater.email}
    </Link>
  );
};

const HistoryStates: React.FC<{ changes: StateChange[] }> = ({ changes }) => {
  const formatMessage = useMessage();
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const hiddenCount = changes.length - VISIBLE_STATES;
  const visible = isExpanded ? changes : changes.slice(0, VISIBLE_STATES);

  return (
    <div className='history-states'>
      {visible.map(({ entry, previousFlow }) => (
        <React.Fragment key={entry.url}>
          <div className='history-state'>
            <time
              className='history-state-time'
              dateTime={entry.updated}
              title={intl.formatDate(entry.updated, FULL_TIME_FORMAT)}
            >
              <FormattedTime
                value={new Date(entry.updated)}
                hour='2-digit'
                minute='2-digit'
                second='2-digit'
              />
            </time>
            <DocBadge docState={entry.state} flow={entry.flow} />
            <span className='history-state-user'>
              <HistoryUpdater updater={entry.updater} />
            </span>
          </div>
          {/* Podřádek téhož záznamu - samostatná položka osy by měla stejný čas jako stav. */}
          {previousFlow && (
            <div className='history-state-flow'>
              <span className='history-flow-dot'></span>
              <span className='history-flow-label'>
                <FormattedMessage {...monitoringMessages.flowChange} />
              </span>
              <span className='history-flow-from'>{previousFlow}</span>
              <span className='history-flow-arrow'>→</span>
              <span className='history-flow-to'>{entry.flow}</span>
            </div>
          )}
        </React.Fragment>
      ))}
      {hiddenCount > 0 && (
        <button
          type='button'
          className='history-inline-btn'
          onClick={() => setIsExpanded(!isExpanded)}
        >
          {isExpanded
            ? formatMessage(monitoringMessages.hideOlderStates)
            : formatMessage(monitoringMessages.showOlderStates, { count: hiddenCount })}
        </button>
      )}
    </div>
  );
};

const HistoryStateGroup: React.FC<{ states: StateChange[] }> = ({ states }) => (
  <div className='history-state-group'>
    <span className='history-knot'></span>
    <HistoryStates changes={states} />
  </div>
);

const HistoryEvent: React.FC<{ event: TimelineEvent; flow: DocHistoryResponse['flow'] }> = ({
  event,
  flow,
}) => {
  const formatMessage = useMessage();
  const intl = useIntl();

  const operationTitle = event.operation_label || event.operation;
  const message = event.message ?? '';
  // Bez názvu operace se titulkem stává první řádek zprávy, aby se nevypsala dvakrát.
  const title = operationTitle || message.split('\n')[0];
  const body = operationTitle ? message : '';

  const timeTitle = [
    intl.formatDate(event.created, FULL_TIME_FORMAT),
    // Přenesené záznamy mají zápis do monitoringu jinde než svůj vznik.
    event.recorded !== event.created
      ? formatMessage(monitoringMessages.recordedAt, {
          recorded: intl.formatDate(event.recorded, FULL_TIME_FORMAT),
        })
      : '',
  ]
    .filter(Boolean)
    .join('\n');

  const iconTitle = [
    formatMessage.lookup(monitoringEventTypeMessages, event.event_type) ?? event.event_type,
    formatMessage.lookup(monitoringDirectionMessages, event.direction) ?? event.direction,
  ].join(' · ');

  return (
    <article className={`history-event level-${event.level ?? 'UNKNOWN'}`}>
      <span className='history-stripe'></span>
      <time className='history-time' dateTime={event.created} title={timeTitle}>
        <FormattedTime
          value={new Date(event.created)}
          hour='2-digit'
          minute='2-digit'
          second='2-digit'
        />
      </time>

      <span className='history-icon' title={iconTitle}>
        {getEventIcon(event)}
      </span>

      <div className='history-main'>
        <div className='history-line'>
          <span className='history-title'>{title}</span>
          {NOTABLE_OUTCOMES.includes(event.outcome) && (
            <span className={`history-outcome outcome-${event.outcome}`}>
              {formatMessage.lookup(monitoringOutcomeMessages, event.outcome) ?? event.outcome}
            </span>
          )}
          {event.changedState && (
            <span className='history-state-change'>
              {formatMessage.lookup(docStateMessages, event.changedState as DOC_STATE, {
                id: flow ?? '',
              }) ?? event.changedState}
            </span>
          )}
          {event.job && (
            <a className='history-job' href={event.job.url} target='_blank' rel='noreferrer'>
              <FormattedMessage {...monitoringMessages.jobLink} values={{ id: event.job.id }} />
            </a>
          )}
        </div>

        {body && <HistoryMessage message={body} />}
        {event.relatedHistory.length > 0 && <HistoryStates changes={event.relatedHistory} />}

        <p className='history-source'>
          {formatMessage.lookup(monitoringSourceMessages, event.source) ?? event.source}
        </p>
      </div>
    </article>
  );
};

type HistoryDayProps = {
  day: TimelineDay;
  isCollapsed: boolean;
  onToggle(key: string): void;
  flow: DocHistoryResponse['flow'];
};

const HistoryDay: React.FC<HistoryDayProps> = ({ day, isCollapsed, onToggle, flow }) => {
  const formatMessage = useMessage();

  // Sbalený den musí říct, kolik toho skrývá, jinak vypadá jako prázdný.
  const recordCount = day.items.reduce(
    (total, item) => total + (item.kind === 'event' ? 1 : item.states.length),
    0,
  );

  return (
    <section className='history-day-group'>
      <h4 className='history-day'>
        <button
          type='button'
          className='history-day-toggle'
          aria-expanded={!isCollapsed}
          onClick={() => onToggle(day.key)}
        >
          <FormattedDate
            value={new Date(day.date)}
            weekday='long'
            day='numeric'
            month='long'
            year='numeric'
          />
          {isCollapsed && (
            <span className='history-day-count'>
              {formatMessage(monitoringMessages.eventCount, { count: recordCount })}
            </span>
          )}
          <span
            className={`history-day-caret ${isCollapsed ? 'is-collapsed' : ''}`}
            aria-hidden='true'
          >
            <Icon element={<SvgCaretDown />} size='sml' />
          </span>
        </button>
      </h4>
      {!isCollapsed &&
        day.items.map((item) =>
          item.kind === 'event' ? (
            <HistoryEvent key={item.key} event={item.event} flow={flow} />
          ) : (
            <HistoryStateGroup key={item.key} states={item.states} />
          ),
        )}
    </section>
  );
};

export const HistoryGraph: React.FC<HistoryGraphProps> = ({ timeline, history = [], flow }) => {
  const formatMessage = useMessage();
  const {
    queryState,
    rows,
    count,
    errorCount,
    warningCount,
    hasMore,
    nextPageCount,
    isLoadingMore,
    isStale,
    loadMore,
    refresh,
  } = timeline;

  /* Sbalené dny, ne rozbalené - dny přibylé stránkováním jsou pak rozbalené samy. */
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());

  const toggleDay = useCallback((key: string) => {
    setCollapsedDays((previous) => {
      const next = new Set(previous);
      if (!next.delete(key)) next.add(key);
      return next;
    });
  }, []);

  const days = useMemo(
    () => buildTimelineDays({ rows, history, hasMore }),
    [hasMore, history, rows],
  );

  /* Hromadné sbalení se drží načtených dnů; ty přibylé stránkováním se ukážou samy. */
  const toggleAllDays = useCallback(() => {
    setCollapsedDays((previous) =>
      days.every((day) => previous.has(day.key)) ? new Set() : new Set(days.map((day) => day.key)),
    );
  }, [days]);

  const isEmpty = rows.length === 0;
  const isFirstPageLoading =
    isEmpty && queryState !== QUERY_STATE.SUCCESS && queryState !== QUERY_STATE.FAIL;

  if (isEmpty && days.length === 0) {
    if (queryState === QUERY_STATE.FAIL) {
      return (
        <DangerAlert>
          <FormattedMessage {...monitoringMessages.timelineFail} />
        </DangerAlert>
      );
    }

    if (isFirstPageLoading) {
      return (
        <WaitingAlert>
          <FormattedMessage {...monitoringMessages.timelineWaiting} />
        </WaitingAlert>
      );
    }

    return (
      <InfoAlert>
        <FormattedMessage {...monitoringMessages.timelineEmpty} />
      </InfoAlert>
    );
  }

  const loadMoreText = isLoadingMore
    ? formatMessage(monitoringMessages.timelineLoadingMore)
    : queryState === QUERY_STATE.FAIL
      ? formatMessage(monitoringMessages.timelineLoadMoreFailed)
      : formatMessage(monitoringMessages.timelineLoadMore, { count: nextPageCount });

  return (
    <div className='history-graph'>
      <div className='history-toolbar'>
        {days.length > 1 && (
          <button type='button' className='history-inline-btn' onClick={toggleAllDays}>
            {formatMessage(
              days.every((day) => collapsedDays.has(day.key))
                ? monitoringMessages.expandAllDays
                : monitoringMessages.collapseAllDays,
            )}
          </button>
        )}
        <span className='history-counts'>
          {formatMessage(monitoringMessages.eventCount, { count })}
          {errorCount > 0 && (
            <>
              {' · '}
              <span className='history-counts-errors'>
                {formatMessage(monitoringMessages.errorCount, { count: errorCount })}
              </span>
            </>
          )}
          {warningCount > 0 && (
            <>
              {' · '}
              <span className='history-counts-warnings'>
                {formatMessage(monitoringMessages.warningCount, { count: warningCount })}
              </span>
            </>
          )}
        </span>
      </div>

      {isStale && (
        <p className='history-stale'>
          <FormattedMessage {...monitoringMessages.timelineStale} />{' '}
          <button type='button' className='history-inline-btn' onClick={refresh}>
            <FormattedMessage {...monitoringMessages.timelineRefresh} />
          </button>
        </p>
      )}

      {isEmpty && queryState === QUERY_STATE.FAIL ? (
        <DangerAlert>
          <FormattedMessage {...monitoringMessages.timelineFail} />
        </DangerAlert>
      ) : isFirstPageLoading ? (
        <WaitingAlert>
          <FormattedMessage {...monitoringMessages.timelineWaiting} />
        </WaitingAlert>
      ) : (
        <div className='history-container'>
          <div className='history-axis'>
            {days.map((day) => (
              <HistoryDay
                key={day.key}
                day={day}
                isCollapsed={collapsedDays.has(day.key)}
                onToggle={toggleDay}
                flow={flow}
              />
            ))}
          </div>
          {hasMore ? (
            <div className='history-axis-fade' aria-hidden='true'>
              <span className='history-ghost' />
              <span className='history-ghost' />
              <span className='history-ghost' />
            </div>
          ) : (
            <span className='history-axis-end'></span>
          )}
        </div>
      )}

      {hasMore && (
        <div className='history-more'>
          <SecondaryBtn
            size='sml'
            icon={loadMoreIcon}
            queryState={isLoadingMore ? 'waiting' : undefined}
            onClick={loadMore}
          >
            {loadMoreText}
          </SecondaryBtn>
          <span className='history-more-count'>
            {formatMessage(monitoringMessages.timelineShown, { shown: rows.length, count })}
          </span>
        </div>
      )}
    </div>
  );
};
